/**
 * @engine/core - Engine
 * 
 * Main engine class that coordinates all systems.
 * Entry point for initialization and lifecycle.
 */

import { GameLoop, LoopState } from './GameLoop';
import { Time } from './Time';
import { LevelManager, levelManager } from './LevelManager';
import { Level } from './Level';

/**
 * Engine configuration
 */
export interface EngineConfig {
  /** Canvas for rendering (optional - can be set later) */
  canvas?: HTMLCanvasElement;
  /** Target FPS for rendering (default: 60) */
  targetFPS?: number;
  /** Fixed timestep in seconds (default: 1/60) */
  fixedDeltaTime?: number;
  /** Auto-start loop on init (default: false) */
  autoStart?: boolean;
}

/**
 * Engine state
 */
export interface EngineState {
  isInitialized: boolean;
  isRunning: boolean;
  isPaused: boolean;
  frameCount: number;
  time: number;
}

/**
 * System interface for engine subsystems
 */
export interface ISystem {
  /** System name for debugging */
  name: string;
  /** Initialize system */
  init?(): Promise<void> | void;
  /** Called each fixed timestep */
  fixedUpdate?(): void;
  /** Called each frame */
  update?(): void;
  /** Called after update */
  lateUpdate?(): void;
  /** Called for rendering */
  render?(): void;
  /** Cleanup system */
  destroy?(): void;
}

/**
 * Engine - Main engine coordinator
 * 
 * Responsibilities:
 * - Initialize all subsystems
 * - Run game loop
 * - Coordinate systems (render, physics, scripts)
 * - Provide global access to core services
 */
export class Engine {
  // Singleton
  private static _instance: Engine | null = null;
  
  // Core systems
  private _gameLoop: GameLoop;
  private _time: Time;
  private _sceneManager: LevelManager;
  
  // Registered systems
  private _systems: Map<string, ISystem> = new Map();
  private _systemOrder: string[] = [];
  
  // State
  private _isInitialized = false;
  private _canvas: HTMLCanvasElement | null = null;
  
  // Event callbacks
  private _onBeforeUpdate: (() => void)[] = [];
  private _onAfterUpdate: (() => void)[] = [];
  
  private constructor() {
    this._time = new Time();
    this._gameLoop = new GameLoop(this._time);
    this._sceneManager = levelManager;
    
    // Setup loop callbacks
    this._gameLoop.setCallbacks({
      fixedUpdate: () => this.fixedUpdate(),
      update: () => this.update(),
      lateUpdate: () => this.lateUpdate(),
      render: () => this.render(),
    });
  }
  
  /**
   * Get Engine singleton
   */
  static get instance(): Engine {
    if (!Engine._instance) {
      Engine._instance = new Engine();
    }
    return Engine._instance;
  }
  
  /**
   * Reset singleton (for testing)
   */
  static reset(): void {
    if (Engine._instance) {
      Engine._instance.destroy();
    }
    Engine._instance = null;
    LevelManager.reset();
  }
  
  // ==========================================================================
  // ACCESSORS
  // ==========================================================================
  
  /** Time management */
  get time(): Time {
    return this._time;
  }
  
  /** Scene manager */
  get scenes(): LevelManager {
    return this._sceneManager;
  }

  /** Active scene shortcut */
  get activeScene(): Level | null {
    return this._sceneManager.activeLevel;
  }
  
  /** Game loop */
  get loop(): GameLoop {
    return this._gameLoop;
  }
  
  /** Canvas element */
  get canvas(): HTMLCanvasElement | null {
    return this._canvas;
  }
  
  /** Is engine initialized */
  get isInitialized(): boolean {
    return this._isInitialized;
  }
  
  /** Is engine running */
  get isRunning(): boolean {
    return this._gameLoop.isRunning;
  }
  
  /** Is engine paused */
  get isPaused(): boolean {
    return this._gameLoop.isPaused;
  }
  
  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================
  
  /**
   * Initialize the engine
   */
  async init(config: EngineConfig = {}): Promise<void> {
    if (this._isInitialized) {
      console.warn('Engine already initialized');
      return;
    }
    
    console.log('[Engine] Initializing...');
    
    // Store config
    this._canvas = config.canvas ?? null;
    
    if (config.fixedDeltaTime) {
      this._time.fixedDeltaTime = config.fixedDeltaTime;
    }
    
    // Initialize all registered systems
    for (const name of this._systemOrder) {
      const system = this._systems.get(name);
      if (system?.init) {
        console.log(`[Engine] Initializing system: ${name}`);
        await system.init();
      }
    }
    
    this._isInitialized = true;
    console.log('[Engine] Initialized');
    
    // Auto-start if requested
    if (config.autoStart) {
      this.start();
    }
  }
  
  /**
   * Set canvas (can be called after init)
   */
  setCanvas(canvas: HTMLCanvasElement): void {
    this._canvas = canvas;
    // TODO: Notify render system
  }
  
  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================
  
  /**
   * Start the engine
   */
  start(): void {
    if (!this._isInitialized) {
      console.warn('Engine not initialized. Call init() first.');
      return;
    }
    
    console.log('[Engine] Starting...');
    this._gameLoop.start();
  }
  
  /**
   * Stop the engine
   */
  stop(): void {
    console.log('[Engine] Stopping...');
    this._gameLoop.stop();
  }
  
  /**
   * Pause the engine
   */
  pause(): void {
    this._gameLoop.pause();
  }
  
  /**
   * Resume the engine
   */
  resume(): void {
    this._gameLoop.resume();
  }
  
  /**
   * Step one frame (for editor)
   */
  step(): void {
    this._gameLoop.step();
  }
  
  /**
   * Destroy engine and cleanup
   */
  destroy(): void {
    console.log('[Engine] Destroying...');
    
    this.stop();
    
    // Destroy systems in reverse order
    for (let i = this._systemOrder.length - 1; i >= 0; i--) {
      const name = this._systemOrder[i];
      const system = this._systems.get(name);
      if (system?.destroy) {
        console.log(`[Engine] Destroying system: ${name}`);
        system.destroy();
      }
    }
    
    this._systems.clear();
    this._systemOrder = [];
    this._isInitialized = false;
  }
  
  // ==========================================================================
  // SYSTEMS
  // ==========================================================================
  
  /**
   * Register a system
   */
  registerSystem(system: ISystem, order?: number): void {
    if (this._systems.has(system.name)) {
      console.warn(`System '${system.name}' already registered`);
      return;
    }
    
    this._systems.set(system.name, system);
    
    if (order !== undefined) {
      this._systemOrder.splice(order, 0, system.name);
    } else {
      this._systemOrder.push(system.name);
    }
  }
  
  /**
   * Unregister a system
   */
  unregisterSystem(name: string): void {
    const system = this._systems.get(name);
    if (system) {
      system.destroy?.();
      this._systems.delete(name);
      
      const index = this._systemOrder.indexOf(name);
      if (index !== -1) {
        this._systemOrder.splice(index, 1);
      }
    }
  }
  
  /**
   * Get a system by name
   */
  getSystem<T extends ISystem>(name: string): T | null {
    return (this._systems.get(name) as T) ?? null;
  }
  
  // ==========================================================================
  // EVENTS
  // ==========================================================================
  
  /**
   * Add callback before update
   */
  onBeforeUpdate(callback: () => void): () => void {
    this._onBeforeUpdate.push(callback);
    return () => {
      const index = this._onBeforeUpdate.indexOf(callback);
      if (index !== -1) this._onBeforeUpdate.splice(index, 1);
    };
  }
  
  /**
   * Add callback after update
   */
  onAfterUpdate(callback: () => void): () => void {
    this._onAfterUpdate.push(callback);
    return () => {
      const index = this._onAfterUpdate.indexOf(callback);
      if (index !== -1) this._onAfterUpdate.splice(index, 1);
    };
  }
  
  // ==========================================================================
  // LOOP CALLBACKS
  // ==========================================================================
  
  private fixedUpdate(): void {
    // Physics/fixed systems
    for (const name of this._systemOrder) {
      const system = this._systems.get(name);
      system?.fixedUpdate?.();
    }
  }
  
  private update(): void {
    // Before update callbacks
    for (const cb of this._onBeforeUpdate) cb();
    
    // Update systems
    for (const name of this._systemOrder) {
      const system = this._systems.get(name);
      system?.update?.();
    }
    
    // Process pending destroys
    for (const scene of this._sceneManager.getAllLevels()) {
      scene.processPendingDestroy();
    }
    
    // After update callbacks
    for (const cb of this._onAfterUpdate) cb();
  }
  
  private lateUpdate(): void {
    // Late update systems
    for (const name of this._systemOrder) {
      const system = this._systems.get(name);
      system?.lateUpdate?.();
    }
  }
  
  private render(): void {
    // Render systems
    for (const name of this._systemOrder) {
      const system = this._systems.get(name);
      system?.render?.();
    }
  }
  
  // ==========================================================================
  // STATE
  // ==========================================================================
  
  /**
   * Get engine state for debugging
   */
  getState(): EngineState {
    return {
      isInitialized: this._isInitialized,
      isRunning: this.isRunning,
      isPaused: this.isPaused,
      frameCount: this._time.frameCount,
      time: this._time.time,
    };
  }
}

// Export singleton accessor
export const engine = Engine.instance;
